import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, getDocs, doc, setDoc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';
import Toast from './Toast';
import Button from './UI/Button';
import Card from './UI/Card';
import Input from './UI/Input';
import { Modal } from './UI';
import {
  CreditCardIcon,
  BanknotesIcon,
  CurrencyDollarIcon,
  QrCodeIcon,
  CheckCircleIcon,
  EyeIcon,
  PhotoIcon,
  CloudArrowUpIcon
} from '@heroicons/react/24/outline';
import { Switch } from '@headlessui/react';

const Settings = () => {
  const [paymentSettings, setPaymentSettings] = useState({
    enabled: false,
    accountNumber: '',
    viber: '',
    qrCodeUrl: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageFile, setImageFile] = useState(null);

  // Check if Firebase Storage is configured
  const isStorageConfigured = process.env.REACT_APP_FIREBASE_STORAGE_BUCKET &&
                               process.env.REACT_APP_FIREBASE_STORAGE_BUCKET !== 'your_storage_bucket_here';

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  useEffect(() => {
    fetchPaymentSettings();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPaymentSettings = async () => {
    setLoading(true);
    try {
      const settingsRef = collection(db, 'settings');
      const settingsSnapshot = await getDocs(settingsRef);
      const settings = settingsSnapshot.docs.find(doc => doc.id === 'payment');

      if (settings) {
        setPaymentSettings(settings.data());
      }
    } catch (error) {
      console.error('Error fetching payment settings:', error);
      showToast('Failed to load settings', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setPaymentSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleImageFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        showToast('Image size should be less than 5MB', 'error');
        return;
      }
      if (!file.type.startsWith('image/')) {
        showToast('Please select a valid image file', 'error');
        return;
      }
      setImageFile(file);
    }
  };

  const uploadImage = async () => {
    if (!imageFile || !isStorageConfigured) return null;

    setUploadingImage(true);
    try {
      const timestamp = Date.now();
      const fileName = `payment-qr-${timestamp}.${imageFile.name.split('.').pop()}`;
      const storageRef = ref(storage, `payment/${fileName}`);

      await uploadBytes(storageRef, imageFile);
      const url = await getDownloadURL(storageRef);

      showToast('QR code image uploaded successfully!', 'success');
      return url;
    } catch (error) {
      console.error('Error uploading image:', error);
      showToast('Failed to upload image', 'error');
      return null;
    } finally {
      setUploadingImage(false);
    }
  };

  const handleUploadAndSave = async () => {
    if (imageFile) {
      const url = await uploadImage();
      if (url) {
        handleInputChange('qrCodeUrl', url);
        setImageFile(null);
      }
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Upload image if there's a new one
      if (imageFile) {
        const url = await uploadImage();
        if (url) {
          paymentSettings.qrCodeUrl = url;
        }
        setImageFile(null);
      }

      const settingsRef = doc(db, 'settings', 'payment');

      // Try to update, if doesn't exist, create it
      try {
        await updateDoc(settingsRef, paymentSettings);
      } catch (error) {
        // Document doesn't exist, create it
        await setDoc(settingsRef, paymentSettings);
      }

      showToast('Payment settings saved successfully!', 'success');
    } catch (error) {
      console.error('Error saving settings:', error);
      showToast('Failed to save settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-text-secondary">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-text-primary mb-2">Settings</h1>
        <p className="text-text-secondary">Manage payment information and system settings</p>
      </div>

      {/* Payment Settings Card */}
      <Card className="mb-6">
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-neutral-200">
          <div className="flex items-center gap-3">
            <CreditCardIcon className="h-8 w-8 text-primary-600" />
            <div>
              <h2 className="text-xl font-bold text-text-primary">Payment Information</h2>
              <p className="text-sm text-text-secondary">Configure payment details for customers</p>
            </div>
          </div>

          {/* Enable/Disable Toggle */}
          <div className="flex items-center gap-3">
            <span className={`text-sm font-medium ${paymentSettings.enabled ? 'text-success-600' : 'text-text-secondary'}`}>
              {paymentSettings.enabled ? 'Enabled' : 'Disabled'}
            </span>
            <Switch
              checked={paymentSettings.enabled}
              onChange={(checked) => handleInputChange('enabled', checked)}
              className={`${
                paymentSettings.enabled ? 'bg-success-500' : 'bg-neutral-300'
              } relative inline-flex h-7 w-12 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2`}
            >
              <span
                className={`${
                  paymentSettings.enabled ? 'translate-x-6' : 'translate-x-1'
                } inline-block h-5 w-5 transform rounded-full bg-white transition-transform`}
              />
            </Switch>
          </div>
        </div>

        {/* Form Fields */}
        <div className="space-y-6">
          {/* Account Number */}
          <div>
            <Input
              label="Bank Account Number"
              value={paymentSettings.accountNumber}
              onChange={(e) => handleInputChange('accountNumber', e.target.value)}
              placeholder="Enter bank account number"
              leftIcon={<BanknotesIcon className="h-5 w-5" />}
              hint="This will be displayed to customers for bank transfers"
            />
          </div>

          {/* Viber */}
          <div>
            <Input
              label="Viber Contact"
              value={paymentSettings.viber}
              onChange={(e) => handleInputChange('viber', e.target.value)}
              placeholder="Enter Viber number or ID"
              leftIcon={<CurrencyDollarIcon className="h-5 w-5" />}
              hint="Customers can use this to contact you for payment confirmation"
            />
          </div>

          {/* QR Code URL */}
          <div>
            <Input
              label="QR Code URL"
              value={paymentSettings.qrCodeUrl}
              onChange={(e) => handleInputChange('qrCodeUrl', e.target.value)}
              placeholder="Paste image URL or upload below"
              leftIcon={<QrCodeIcon className="h-5 w-5" />}
              hint="URL to your payment QR code image"
            />
          </div>

          {/* QR Code Upload */}
          {isStorageConfigured && (
            <Card className="bg-neutral-50">
              <div className="flex items-start gap-4">
                <PhotoIcon className="h-6 w-6 text-primary-600 flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <h3 className="font-medium text-text-primary mb-2">Upload QR Code Image</h3>
                  <p className="text-sm text-text-secondary mb-4">
                    Upload your payment QR code image (max 5MB)
                  </p>

                  <div className="flex items-center gap-3">
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageFileChange}
                        className="hidden"
                      />
                      <div className="px-4 py-2 bg-white border-2 border-primary-500 text-primary-600 rounded-lg font-medium hover:bg-primary-50 transition-colors inline-flex items-center gap-2">
                        <CloudArrowUpIcon className="h-5 w-5" />
                        Choose Image
                      </div>
                    </label>

                    {imageFile && (
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-text-secondary">{imageFile.name}</span>
                        <Button
                          size="sm"
                          onClick={handleUploadAndSave}
                          loading={uploadingImage}
                          leftIcon={<CloudArrowUpIcon className="h-4 w-4" />}
                        >
                          Upload
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* QR Code Preview */}
          {paymentSettings.qrCodeUrl && (
            <Card className="bg-neutral-50">
              <h3 className="font-medium text-text-primary mb-3">QR Code Preview</h3>
              <div className="flex justify-center p-4 bg-white rounded-lg">
                <img
                  src={paymentSettings.qrCodeUrl}
                  alt="Payment QR Code"
                  className="max-w-xs w-full rounded-lg shadow-md"
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/300x300?text=Invalid+QR+Code+URL';
                  }}
                />
              </div>
            </Card>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 mt-8 pt-6 border-t border-neutral-200">
          <Button
            variant="primary"
            size="lg"
            onClick={handleSave}
            loading={saving}
            leftIcon={<CheckCircleIcon className="h-5 w-5" />}
          >
            Save Settings
          </Button>

          <Button
            variant="outline"
            size="lg"
            onClick={() => setShowPreview(true)}
            disabled={!paymentSettings.enabled}
            leftIcon={<EyeIcon className="h-5 w-5" />}
          >
            Preview
          </Button>
        </div>
      </Card>

      {/* Information Card */}
      <Card className="bg-info-50 border-info-200">
        <div className="flex gap-3">
          <div className="flex-shrink-0">
            <div className="h-10 w-10 rounded-full bg-info-100 flex items-center justify-center">
              <span className="text-xl">💡</span>
            </div>
          </div>
          <div>
            <h3 className="font-medium text-info-900 mb-2">How it works</h3>
            <ul className="text-sm text-info-800 space-y-1">
              <li>• Enable payment information to show it to customers and cashiers</li>
              <li>• Customers will see a "View Payment Info" button in their cart</li>
              <li>• Cashiers can access payment details to show customers</li>
              <li>• All changes are saved to the database and take effect immediately</li>
            </ul>
          </div>
        </div>
      </Card>

      {/* Preview Modal */}
      <Modal
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        title="Payment Information Preview"
        size="md"
      >
        <div className="space-y-4">
          <div className="bg-info-50 border border-info-200 rounded-lg p-4">
            <p className="text-sm text-info-800">
              This is how customers will see your payment information
            </p>
          </div>

          {paymentSettings.accountNumber && (
            <Card className="bg-neutral-50">
              <div className="flex items-center gap-3 mb-2">
                <BanknotesIcon className="h-6 w-6 text-primary-600" />
                <p className="text-sm font-medium text-text-secondary">Account Number</p>
              </div>
              <p className="text-xl font-bold text-text-primary">
                {paymentSettings.accountNumber}
              </p>
            </Card>
          )}

          {paymentSettings.viber && (
            <Card className="bg-neutral-50">
              <div className="flex items-center gap-3 mb-2">
                <CurrencyDollarIcon className="h-6 w-6 text-success-600" />
                <p className="text-sm font-medium text-text-secondary">Viber</p>
              </div>
              <p className="text-xl font-bold text-text-primary">
                {paymentSettings.viber}
              </p>
            </Card>
          )}

          {paymentSettings.qrCodeUrl && (
            <Card className="bg-neutral-50">
              <p className="text-sm font-medium text-text-secondary mb-3 text-center">
                QR Code for Payment
              </p>
              <div className="flex justify-center">
                <img
                  src={paymentSettings.qrCodeUrl}
                  alt="Payment QR Code"
                  className="max-w-xs w-full rounded-lg shadow-md"
                />
              </div>
            </Card>
          )}

          {!paymentSettings.accountNumber && !paymentSettings.viber && !paymentSettings.qrCodeUrl && (
            <div className="text-center py-8">
              <p className="text-text-secondary">No payment information configured</p>
              <p className="text-sm text-text-muted mt-2">Add payment details to see the preview</p>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default Settings;
